import {
  LQG_THRESHOLD_ACUTE_HAZARDOUS_LB,
  LQG_THRESHOLD_HAZARDOUS_LB,
} from "encamp-shared/src/constants/wasteGeneratorStatusThresholds";
import { stateWasteGeneratorStatusToLabel } from "encamp-shared/src/waste";
import {
  GeneratorStatusExceedanceDirection,
  RcraInfoFederalWasteGeneratorStatus,
} from "generated-graphql/graphql";

/** A state increase qualifies even when the federal comparison is downward; the server applies the same rule. */
export function canFileNotification(row: {
  direction?: GeneratorStatusExceedanceDirection | null;
  stateDirection?: GeneratorStatusExceedanceDirection | null;
}): boolean {
  return (
    row.direction === GeneratorStatusExceedanceDirection.Upward ||
    row.stateDirection === GeneratorStatusExceedanceDirection.Upward
  );
}

/** Acute waste can drive LQG even when total waste is below the tonnage threshold. */
export function isAcuteDriven(
  federalStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined,
  acuteLbs: number | null | undefined,
  totalLbs: number | null | undefined
): boolean {
  return (
    federalStatus === RcraInfoFederalWasteGeneratorStatus.LargeQuantityGenerator &&
    acuteLbs != null &&
    acuteLbs >= LQG_THRESHOLD_ACUTE_HAZARDOUS_LB &&
    (totalLbs == null || totalLbs < LQG_THRESHOLD_HAZARDOUS_LB)
  );
}

export function formatLbs(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

export type EpisodicExemptLbs = {
  episodicHazardousExemptLbs?: number | null;
  episodicAcuteExemptLbs?: number | null;
  episodicStateRegulatedExemptLbs?: number | null;
};

export function episodicExemptLabel(lbs: EpisodicExemptLbs): string | null {
  const parts: [number | null | undefined, string][] = [
    [lbs.episodicHazardousExemptLbs, "hazardous"],
    [lbs.episodicAcuteExemptLbs, "acute"],
    [lbs.episodicStateRegulatedExemptLbs, "state-regulated"],
  ];
  const shown = parts.flatMap(([lb, kind]) =>
    lb != null && lb > 0 ? [`${formatLbs(lb)} lbs ${kind}`] : []
  );
  return shown.length > 0 ? `Episodic exempt: ${shown.join(", ")}` : null;
}

const ABOVE_REPORTED = {
  label: "Above reported status",
  color: "error",
  relation: "ABOVE",
} satisfies Comparison;

const BELOW_REPORTED = {
  label: "Below reported status",
  color: "info",
  relation: "BELOW",
} satisfies Comparison;

export function comparisonDescription(
  reported: string | null | undefined,
  calculated: string | null | undefined,
  rank: (status: string | null) => number,
  direction?: string | null,
  calculationAvailable = false
): Comparison {
  if (direction === "UPWARD") return ABOVE_REPORTED;
  if (direction === "DOWNWARD") return BELOW_REPORTED;
  if (!calculated && !calculationAvailable)
    return { label: "Calculation unavailable", color: "info" };
  if (!reported || reported === "PENDING" || reported === "UNDETERMINED")
    return { label: "Reported status unavailable", color: "info" };
  const difference = rank(calculated ?? null) - rank(reported);
  if (difference > 0) return ABOVE_REPORTED;
  if (difference < 0) return BELOW_REPORTED;
  if (reported !== calculated && rank(reported) === 0)
    return { label: "Comparison unavailable", color: "info" };
  return { label: "Matches reported status", color: "success" };
}

/** A calculated month with no status had no waste counting toward that axis's thresholds. */
export function calculatedStatusLabel(
  status: string | null | undefined,
  calculationAvailable: boolean
): string {
  if (status || calculationAvailable) return stateWasteGeneratorStatusToLabel(status ?? null);
  return "Unavailable";
}

export type ComparisonRelation = "ABOVE" | "BELOW";

type Comparison = {
  label: string;
  color: "info" | "error" | "success";
  /** Set only when the calculated status ranks above or below the reported one. */
  relation?: ComparisonRelation;
};

export function determinationFactors(
  value: unknown,
  axis: "federal" | "state"
): Record<string, number> {
  if (typeof value !== "object" || value === null) return {};
  const factors: unknown = Reflect.get(value, axis);
  if (typeof factors !== "object" || factors === null) return {};
  return Object.fromEntries(
    Object.entries(factors).filter(
      (entry): entry is [string, number] =>
        typeof entry[1] === "number" && Number.isFinite(entry[1])
    )
  );
}
